import React, { useState } from 'react'
import {
  Search,
  ArrowRightLeft,
  Upload,
  Download,
  Eye,
  CheckCircle,
  Clock,
  Trash2,
} from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/app/components/ui/dialog'
import { Badge } from '@/app/components/ui/badge'
import { Button } from '@/app/components/ui/button'
import { Input } from '@/app/components/ui/input'
import { Textarea } from '@/app/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select'
import { PendingTransfer, InventoryItem } from '@/app/services/inventory.service'

interface TransferModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  inventory: InventoryItem[]
  pendingSends: PendingTransfer[]
  pendingReceives: PendingTransfer[]
  onSendReview: (transfer: PendingTransfer) => void
  onReceiveConfirm: (transfer: PendingTransfer) => void
  onTransfer: (data: {
    action: 'request' | 'receive' | 'send'
    products: Array<{ productId: string; quantity: string }>
    fromBranch: string
    toBranch: string
    notes: string
  }) => void
}

export function TransferModal({
  open,
  onOpenChange,
  inventory,
  pendingSends,
  pendingReceives,
  onSendReview,
  onReceiveConfirm,
  onTransfer,
}: TransferModalProps) {
  const [transferAction, setTransferAction] = useState<'request' | 'receive' | 'send'>('send')
  const [transferProducts, setTransferProducts] = useState<
    Array<{ productId: string; quantity: string }>
  >([{ productId: '', quantity: '' }])
  const [transferData, setTransferData] = useState({
    fromBranch: '',
    toBranch: '',
    notes: '',
  })
  const [productSearchTerm, setProductSearchTerm] = useState('')

  const handleTransfer = () => {
    onTransfer({
      action: transferAction,
      products: transferProducts,
      ...transferData,
    })
    setTransferProducts([{ productId: '', quantity: '' }])
    setTransferData({
      fromBranch: '',
      toBranch: '',
      notes: '',
    })
  }

  const removeTransferProduct = (index: number) => {
    setTransferProducts(transferProducts.filter((_, i) => i !== index))
  }

  const updateTransferProduct = (
    index: number,
    field: 'productId' | 'quantity',
    value: string,
  ) => {
    const updated = [...transferProducts]
    updated[index][field] = value
    setTransferProducts(updated)
  }

  const addProductFromSearch = (productId: string) => {
    if (transferAction === 'request' && transferData.fromBranch) {
      const item = inventory.find((i) => i.id.toString() === productId)
      if (!item) return

      let availableStock = 0
      if (transferData.fromBranch === 'centro') availableStock = item.stockCentro
      else if (transferData.fromBranch === 'norte') availableStock = item.stockNorte
      else if (transferData.fromBranch === 'sur') availableStock = item.stockSur

      if (availableStock <= 0) {
        alert(
          `El producto "${item.name}" no está disponible en ${transferData.fromBranch}. Stock: ${availableStock}`,
        )
        return
      }
    }

    const existingIndex = transferProducts.findIndex((p) => p.productId === productId)
    if (existingIndex >= 0) {
      const updated = [...transferProducts]
      const currentQty = parseInt(updated[existingIndex].quantity) || 0
      updated[existingIndex].quantity = (currentQty + 1).toString()
      setTransferProducts(updated)
    } else {
      setTransferProducts([...transferProducts, { productId, quantity: '1' }])
    }
    setProductSearchTerm('')
  }

  const handleClose = () => {
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>Gestión de Transferencias</DialogTitle>
          <DialogDescription className="sr-only">
            Solicitar, recibir o enviar productos entre sucursales
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-6">
          {/* Transfer Action Type */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">
              Tipo de Operación:
            </label>
            <div className="grid grid-cols-3 gap-2">
              <button
                onClick={() => setTransferAction('send')}
                className={`rounded-lg border-2 p-3 transition-all ${
                  transferAction === 'send'
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <ArrowRightLeft className="mx-auto mb-1 h-5 w-5 text-blue-600" />
                <p className="text-xs font-medium">Enviar</p>
                <p className="text-xs text-slate-600">A otra sucursal</p>
              </button>
              <button
                onClick={() => setTransferAction('request')}
                className={`rounded-lg border-2 p-3 transition-all ${
                  transferAction === 'request'
                    ? 'border-green-500 bg-green-50'
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <Download className="mx-auto mb-1 h-5 w-5 text-green-600" />
                <p className="text-xs font-medium">Solicitar</p>
                <p className="text-xs text-slate-600">De otra sucursal</p>
              </button>
              <button
                onClick={() => setTransferAction('receive')}
                className={`rounded-lg border-2 p-3 transition-all ${
                  transferAction === 'receive'
                    ? 'border-purple-500 bg-purple-50'
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <Upload className="mx-auto mb-1 h-5 w-5 text-purple-600" />
                <p className="text-xs font-medium">Recibir</p>
                <p className="text-xs text-slate-600">De otra sucursal</p>
              </button>
            </div>
          </div>

          {/* Pending Sends - Only show for Send action */}
          {transferAction === 'send' && pendingSends.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-orange-600" />
                <h4 className="text-sm font-medium text-slate-900">
                  Pendientes de Envío
                </h4>
              </div>
              <div className="max-h-48 overflow-y-auto rounded-lg border border-orange-200 bg-orange-50 p-3">
                {pendingSends.map((transfer) => (
                  <div
                    key={transfer.id}
                    className="mb-2 rounded-lg bg-white p-3 last:mb-0"
                  >
                    <div className="mb-2 flex items-start justify-between">
                      <div>
                        <p className="text-sm font-medium text-slate-900">
                          {transfer.id}
                        </p>
                        <p className="text-xs text-slate-600">
                          {transfer.date}
                        </p>
                      </div>
                      <Badge className="bg-orange-100 text-orange-700">
                        Pendiente
                      </Badge>
                    </div>
                    <p className="mb-2 text-xs text-slate-700">
                      De: {transfer.fromBranch} → A: {transfer.toBranch}
                    </p>
                    <div className="space-y-1">
                      {transfer.products.map((product, idx) => (
                        <p key={idx} className="text-xs text-slate-600">
                          • {product.name} ({product.quantity} uds)
                        </p>
                      ))}
                    </div>
                    <Button
                      size="sm"
                      className="mt-2 w-full"
                      variant="outline"
                      onClick={() => onSendReview(transfer)}
                    >
                      <Eye className="mr-1 h-3 w-3" />
                      Revisar Solicitud
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Pending Receives - Only show for Receive action */}
          {transferAction === 'receive' && pendingReceives.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-purple-600" />
                <h4 className="text-sm font-medium text-slate-900">
                  Pendientes por Recibir
                </h4>
              </div>
              <div className="max-h-48 overflow-y-auto rounded-lg border border-purple-200 bg-purple-50 p-3">
                {pendingReceives.map((transfer) => (
                  <div
                    key={transfer.id}
                    className="mb-2 rounded-lg bg-white p-3 last:mb-0"
                  >
                    <div className="mb-2 flex items-start justify-between">
                      <div>
                        <p className="text-sm font-medium text-slate-900">
                          {transfer.id}
                        </p>
                        <p className="text-xs text-slate-600">
                          {transfer.date}
                        </p>
                      </div>
                      <Badge className="bg-purple-100 text-purple-700">
                        Por Recibir
                      </Badge>
                    </div>
                    <p className="mb-2 text-xs text-slate-700">
                      De: {transfer.fromBranch} → A: {transfer.toBranch}
                    </p>
                    <div className="space-y-1">
                      {transfer.products.map((product, idx) => (
                        <p key={idx} className="text-xs text-slate-600">
                          • {product.name} ({product.quantity} uds)
                        </p>
                      ))}
                    </div>
                    <Button
                      size="sm"
                      className="mt-2 w-full"
                      variant="outline"
                      onClick={() => onReceiveConfirm(transfer)}
                    >
                      <CheckCircle className="mr-1 h-3 w-3" />
                      Confirmar Recepción
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Branch Selection - Only for Send and Request */}
          {(transferAction === 'send' || transferAction === 'request') && (
            <div className="grid grid-cols-2 gap-4">
              {transferAction === 'send' && (
                <>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      Desde (Mi Sucursal)
                    </label>
                    <Select
                      value={transferData.fromBranch}
                      onValueChange={(value) =>
                        setTransferData({
                          ...transferData,
                          fromBranch: value,
                        })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Sucursal origen" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="centro">Sucursal Centro</SelectItem>
                        <SelectItem value="norte">Sucursal Norte</SelectItem>
                        <SelectItem value="sur">Sucursal Sur</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      Hacia
                    </label>
                    <Select
                      value={transferData.toBranch}
                      onValueChange={(value) =>
                        setTransferData({ ...transferData, toBranch: value })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Sucursal destino" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="centro">Sucursal Centro</SelectItem>
                        <SelectItem value="norte">Sucursal Norte</SelectItem>
                        <SelectItem value="sur">Sucursal Sur</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}
              {transferAction === 'request' && (
                <>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      Solicitar De
                    </label>
                    <Select
                      value={transferData.fromBranch}
                      onValueChange={(value) =>
                        setTransferData({
                          ...transferData,
                          fromBranch: value,
                        })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Sucursal origen" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="centro">Sucursal Centro</SelectItem>
                        <SelectItem value="norte">Sucursal Norte</SelectItem>
                        <SelectItem value="sur">Sucursal Sur</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      Para (Mi Sucursal)
                    </label>
                    <Select
                      value={transferData.toBranch}
                      onValueChange={(value) =>
                        setTransferData({ ...transferData, toBranch: value })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Mi sucursal" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="centro">Sucursal Centro</SelectItem>
                        <SelectItem value="norte">Sucursal Norte</SelectItem>
                        <SelectItem value="sur">Sucursal Sur</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Products Selection - Only for Send and Request */}
          {(transferAction === 'send' || transferAction === 'request') && (
            <div className="space-y-3">
              <label className="text-sm font-medium text-slate-700">
                Productos:
              </label>

              {/* Product Search */}
              <div className="relative">
                <Search className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input
                  value={productSearchTerm}
                  onChange={(e) => setProductSearchTerm(e.target.value)}
                  placeholder="Buscar por nombre o código de barras..."
                  className="pl-10"
                />
                {/* Search Results Dropdown */}
                {productSearchTerm && (
                  <div className="absolute z-10 mt-1 max-h-60 w-full overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-lg">
                    {inventory
                      .filter(
                        (item) =>
                          item.name
                            .toLowerCase()
                            .includes(productSearchTerm.toLowerCase()) ||
                          (item.lot &&
                            item.lot
                              .toLowerCase()
                              .includes(productSearchTerm.toLowerCase())),
                      )
                      .map((item) => {
                        let displayStock = item.stock
                        let isAvailable = true

                        if (
                          transferAction === 'request' &&
                          transferData.fromBranch
                        ) {
                          if (transferData.fromBranch === 'centro')
                            displayStock = item.stockCentro
                          else if (transferData.fromBranch === 'norte')
                            displayStock = item.stockNorte
                          else if (transferData.fromBranch === 'sur')
                            displayStock = item.stockSur

                          isAvailable = displayStock > 0
                        }

                        return (
                          <button
                            key={item.id}
                            onClick={() =>
                              addProductFromSearch(item.id.toString())
                            }
                            disabled={!isAvailable && transferAction === 'request'}
                            className={`w-full border-b border-slate-100 px-4 py-2 text-left last:border-b-0 ${
                              !isAvailable && transferAction === 'request'
                                ? 'cursor-not-allowed bg-red-50 opacity-60'
                                : 'hover:bg-slate-50'
                            }`}
                          >
                            <p
                              className={`text-sm font-medium ${!isAvailable && transferAction === 'request' ? 'text-red-700' : 'text-slate-900'}`}
                            >
                              {item.name}
                              {!isAvailable && transferAction === 'request' && (
                                <span className="ml-2 text-xs text-red-600">
                                  (No disponible)
                                </span>
                              )}
                            </p>
                            <p className="text-xs text-slate-600">
                              Stock: {displayStock} | Lote: {item.lot || 'N/A'}
                            </p>
                          </button>
                        )
                      })}
                    {inventory.filter(
                      (item) =>
                        item.name
                          .toLowerCase()
                          .includes(productSearchTerm.toLowerCase()) ||
                        (item.lot &&
                          item.lot
                            .toLowerCase()
                            .includes(productSearchTerm.toLowerCase())),
                    ).length === 0 && (
                      <div className="px-4 py-3 text-center text-sm text-slate-600">
                        No se encontraron productos
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Selected Products */}
              <div className="max-h-60 space-y-2 overflow-y-auto rounded-lg border border-slate-200 p-3">
                {transferProducts.length > 0 &&
                transferProducts.some((p) => p.productId) ? (
                  transferProducts
                    .filter((p) => p.productId)
                    .map((product, index) => {
                      const inventoryItem = inventory.find(
                        (i) => i.id.toString() === product.productId,
                      )

                      let displayStock = inventoryItem?.stock || 0
                      if (
                        transferAction === 'request' &&
                        transferData.fromBranch &&
                        inventoryItem
                      ) {
                        if (transferData.fromBranch === 'centro')
                          displayStock = inventoryItem.stockCentro
                        else if (transferData.fromBranch === 'norte')
                          displayStock = inventoryItem.stockNorte
                        else if (transferData.fromBranch === 'sur')
                          displayStock = inventoryItem.stockSur
                      }

                      return (
                        <div
                          key={index}
                          className="flex items-center gap-2 rounded-lg bg-slate-50 p-3"
                        >
                          <div className="flex-1">
                            <p className="text-sm font-medium text-slate-900">
                              {inventoryItem?.name}
                            </p>
                            <p className="text-xs text-slate-600">
                              Stock disponible
                              {transferAction === 'request' &&
                              transferData.fromBranch
                                ? ` en ${transferData.fromBranch}`
                                : ''}
                              : {displayStock}
                            </p>
                          </div>
                          <div className="w-24">
                            <Input
                              type="number"
                              value={product.quantity}
                              onChange={(e) =>
                                updateTransferProduct(
                                  index,
                                  'quantity',
                                  e.target.value,
                                )
                              }
                              placeholder="Cant."
                              min="1"
                              max={displayStock}
                            />
                          </div>
                          <Button
                            onClick={() => removeTransferProduct(index)}
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:bg-red-50 hover:text-red-700"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      )
                    })
                ) : (
                  <p className="py-4 text-center text-sm text-slate-600">
                    Busca y agrega productos usando el campo de búsqueda
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Notes - Only for Send and Request */}
          {(transferAction === 'send' || transferAction === 'request') && (
            <>
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">
                  Notas
                </label>
                <Textarea
                  value={transferData.notes}
                  onChange={(e) =>
                    setTransferData({
                      ...transferData,
                      notes: e.target.value,
                    })
                  }
                  placeholder="Notas adicionales sobre la transferencia..."
                  rows={3}
                />
              </div>

              {/* Summary */}
              <div className="rounded-lg bg-slate-50 p-4">
                <p className="mb-2 text-sm font-medium text-slate-900">
                  Resumen:
                </p>
                <div className="space-y-1 text-sm text-slate-700">
                  <p>
                    <strong>Operación:</strong>{' '}
                    {transferAction === 'send'
                      ? 'Enviar productos'
                      : 'Solicitar productos'}
                  </p>
                  <p>
                    <strong>Productos:</strong>{' '}
                    {transferProducts.filter((p) => p.productId).length}
                  </p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <Button onClick={handleTransfer} className="flex-1">
                  {transferAction === 'send' && 'Enviar Productos'}
                  {transferAction === 'request' && 'Crear Solicitud'}
                </Button>
                <Button
                  variant="outline"
                  onClick={handleClose}
                  className="flex-1"
                >
                  Cancelar
                </Button>
              </div>
            </>
          )}

          {/* Close button for Receive mode */}
          {transferAction === 'receive' && (
            <div className="flex justify-end">
              <Button variant="outline" onClick={handleClose}>
                Cerrar
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
